import type { ReactElement } from "react";
import { ErrorState } from "./state-components/error-state";
import { usePageLayoutState } from "./hooks/use-page-layout-state";
import { PageHeader } from "./shared-components/page-header";
import { PageWrapper } from "./shared-components/page-wrapper";
import type { CardListWithHeaderPageLayoutProps } from "./types";

/**
 * CardListWithHeaderPageLayout - Layout for list pages that need to render
 * children even when the data array is empty.
 *
 * Use this when you have complementary UI (eligibility cards, action buttons)
 * that should always be visible alongside a potentially empty list.
 *
 * @example
 * ```tsx
 * const FundingRequests = () => {
 *   const { data, fetching } = useAppSelector(selectFundingRequests);
 *
 *   return (
 *     <CardListWithHeaderPageLayout
 *       fetching={fetching}
 *       data={data}
 *       title="Demandes de financement"
 *       loadingComponent={<FundingRequestsSkeleton />}
 *     >
 *       {(requests) => (
 *         <>
 *           <EligibilityCard />
 *           <CardListSection items={requests} ... />
 *         </>
 *       )}
 *     </CardListWithHeaderPageLayout>
 *   );
 * };
 * ```
 */
export function CardListWithHeaderPageLayout<T>({
  fetching,
  data,
  title,
  subtitle,
  children,
  loadingComponent,
  errorStateProps,
  className,
}: CardListWithHeaderPageLayoutProps<T>): ReactElement {
  const { isLoading, isError } = usePageLayoutState({
    fetching,
    idleAsLoading: true,
  });

  return (
    <PageWrapper className={className}>
      <PageHeader title={title} subtitle={subtitle} />
      {isLoading && loadingComponent}
      {isError && <ErrorState {...errorStateProps} />}
      {!isLoading && !isError && children(data ?? [])}
    </PageWrapper>
  );
}
